import { useEffect, useState } from 'react'
import { Printer, AlertTriangle } from 'lucide-react'
import { thDay, groupByMonth } from '../../lib/followUpDoc.js'

/**
 * /reports/reimbursement/followup-print — the A4 ออกเอกสารติดตาม letter. Built by FollowUpLetter.jsx and handed over through localStorage, exactly like
 * SsoImportPrint.jsx — read that file's readReport() for why the key is bounded by AGE and
 * never deleted on read (StrictMode double-mount, and Ctrl+R on this tab).
 *
 * Registered OUTSIDE <Layout> in App.jsx, or the sidebar prints too.
 *
 * ONE LETTER, ONE LETTERHEAD (บริษัท สไมล์ซีซันส์ จำกัด), sections by เดือน/ปี of the visit,
 * and NO branch or รายการที่เบิก column — Ram's calls, 2026-10-14 (the payer looks a claim
 * up by its เลขที่เคลม; the procedures stay on the selection screen only). No signature block: the letter names who
 * is following up, with a phone and the insurance mailbox, and that is what a payer needs
 * to answer it.
 */

export const FOLLOWUP_STORAGE_KEY = 'reimbursement-followup:print'
const MAX_AGE_MS = 2 * 60 * 60 * 1000

function readDoc() {
  let raw
  try { raw = localStorage.getItem(FOLLOWUP_STORAGE_KEY) } catch { return null }
  if (!raw) return null
  let parsed
  try { parsed = JSON.parse(raw) } catch { return null }
  if (!parsed?.doc) return null
  if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
    try { localStorage.removeItem(FOLLOWUP_STORAGE_KEY) } catch { /* nothing to do */ }
    return null
  }
  return parsed.doc
}

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/* ⚠️ print resets the sheet's max-width and padding — the screen frame must not become a
   wasted right margin on paper (feedback: PDF generation, no wasted right margin). */
const PRINT_CSS = `
@page { size: A4 portrait; margin: 12mm 11mm 12mm 11mm; }
@media print {
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { box-shadow: none !important; border: 0 !important; margin: 0 !important; padding: 0 !important; max-width: none !important; border-radius: 0 !important; }
}
.sheet { font-size: 10.5px; line-height: 1.5; color: #111; font-family: 'Sarabun', 'Noto Sans Thai', 'Prompt', system-ui, sans-serif; }
.month td { background: #e2e8f0 !important; font-weight: 700; font-size: 10px; padding: 5px 4px; border-bottom: 0.8px solid #94a3b8; }
.month .num { font-weight: 700; }
.grand td { border-top: 1.4px solid #1e293b; border-bottom: 1.4px solid #1e293b; font-weight: 700; font-size: 11px; background: #fff !important; padding: 6px 4px; }
.remark { margin-top: 10px; border: 0.8px solid #cbd5e1; border-radius: 6px; padding: 6px 10px; white-space: pre-wrap; break-inside: avoid; }
.contact { margin-top: 14px; padding-top: 8px; border-top: 0.8px solid #cbd5e1; display: grid; grid-template-columns: 70px 1fr; row-gap: 2px; break-inside: avoid; }
.contact b { font-weight: 600; }
.lh { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 8px; border-bottom: 1.5px solid #1e293b; }
.lh .co { font-size: 13.5px; font-weight: 700; color: #0f172a; }
.lh .sub { font-size: 10px; color: #475569; }
.lh .meta { text-align: right; font-size: 10px; color: #475569; white-space: nowrap; }
.title { text-align: center; font-size: 14px; font-weight: 700; margin: 14px 0 10px; color: #0f172a; }
.kv { display: grid; grid-template-columns: 52px 1fr; row-gap: 2px; margin-bottom: 8px; }
.kv b { font-weight: 600; }
.note { text-indent: 2.5em; margin: 6px 0 10px; text-align: justify; }
.facts { display: flex; flex-wrap: wrap; gap: 6px 18px; background: #f1f5f9; border-radius: 6px; padding: 6px 10px; margin-bottom: 8px; font-size: 10px; }
.facts b { font-weight: 700; }
table { width: 100%; border-collapse: collapse; table-layout: fixed; }
thead { display: table-header-group; }
tr { break-inside: avoid; }
th { background: #1e293b; color: #fff; font-weight: 600; font-size: 9.5px; padding: 5px 4px; text-align: left; }
td { padding: 4px 4px; border-bottom: 0.5px solid #cbd5e1; vertical-align: top; font-size: 9.5px; word-wrap: break-word; }
tbody tr:nth-child(even) td { background: #f8fafc; }
.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.mono { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 9px; }
.c { text-align: center; }
.old { color: #b91c1c; font-weight: 600; }
.small { font-size: 9px; color: #64748b; }
`

export default function FollowUpPrint() {
  const [doc, setDoc] = useState(undefined)
  useEffect(() => { setDoc(readDoc()) }, [])
  useEffect(() => {
    if (doc) document.title = `ติดตามค้างชำระ ${doc.payer} ${doc.issued}`
  }, [doc])

  if (doc === undefined) return null
  const months = doc ? groupByMonth(doc.rows || []) : []
  // ลำดับ runs 1…N across the whole letter, in print order
  const seq = new Map()
  months.forEach((m) => m.rows.forEach((r) => seq.set(r.id, seq.size + 1)))
  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-[13.5px] text-amber-800 flex gap-2">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          ไม่พบข้อมูลเอกสาร (หมดอายุหลัง 2 ชั่วโมง) — กลับไปที่ รายการตามเบิก › ออกเอกสารติดตาม แล้วกด สร้างเอกสาร ใหม่
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4 print:bg-white print:p-0">
      <style>{PRINT_CSS}</style>

      <div className="no-print max-w-[794px] mx-auto mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 bg-white rounded-xl border border-slate-200 px-4 py-3">
        <button type="button" onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white px-3.5 py-2 text-[13px] font-medium">
          <Printer size={15} /> พิมพ์ / บันทึก PDF
        </button>
        <span className="text-[12.5px] text-slate-600">
          {doc.payer} · {doc.count} รายการ · ฿{fmt(doc.total)}
        </span>
        <span className="text-[11.5px] text-slate-400 basis-full">
          ในกล่องพิมพ์ เลือกปลายทาง “Save as PDF” · ขนาด A4 · ปิด “Headers and footers” เพื่อไม่ให้ URL ติดมาในเอกสาร
        </span>
      </div>

      <div className="sheet max-w-[794px] mx-auto bg-white rounded-lg shadow-lg p-10">
        <div className="lh">
          <div>
            <div className="co">{doc.company?.name}</div>
            {doc.company?.address ? <div className="sub">{doc.company.address}</div> : null}
            {doc.company?.tax ? <div className="sub">เลขประจำตัวผู้เสียภาษี {doc.company.tax}</div> : null}
          </div>
          <div className="meta">
            <div>วันที่ {thDay(doc.issued, { full: true })}</div>
          </div>
        </div>

        <div className="title">หนังสือติดตามรายการค่ารักษาพยาบาลค้างชำระ</div>

        <div className="kv">
          <b>เรื่อง</b><span>ขอติดตามรายการเรียกเก็บค่ารักษาพยาบาลที่ยังไม่ได้รับชำระ</span>
          <b>เรียน</b><span>{doc.to}</span>
        </div>

        <p className="note">{doc.body}</p>

        <div className="facts">
          <span>ผู้จ่าย <b>{doc.payer}</b></span>
          <span>วันที่รักษา <b>{thDay(doc.start)} – {thDay(doc.end)}</b></span>
          <span>จำนวน <b>{doc.count}</b> รายการ</span>
          <span>รวม <b>{fmt(doc.total)}</b> บาท</span>
        </div>

        <table>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '21%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="c">ลำดับ</th>
              <th>วันที่รักษา</th>
              <th>ชื่อ-นามสกุล</th>
              <th>HN</th>
              <th>ทันตแพทย์</th>
              <th>เลขที่เคลม</th>
              <th className="num">ค้าง (วัน)</th>
              <th className="num">ยอดเบิก (บาท)</th>
            </tr>
          </thead>
          {months.map((m) => (
            <tbody key={m.month}>
              <tr className="month">
                <td colSpan={6}>{m.label} · {m.rows.length} รายการ</td>
                <td colSpan={2} className="num">{fmt(m.total)}</td>
              </tr>
              {m.rows.map((r) => (
                <tr key={r.id}>
                  <td className="c">{seq.get(r.id)}</td>
                  <td>{thDay(r.day)}</td>
                  <td>{r.patient || '—'}</td>
                  <td className="mono">{r.hn || '—'}</td>
                  <td>{r.doctor || '—'}</td>
                  <td className="mono">{r.claimRef || '—'}</td>
                  <td className={`num ${r.age > 90 ? 'old' : ''}`}>{r.age ?? '—'}</td>
                  <td className="num">{fmt(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          ))}
          <tbody>
            <tr className="grand">
              <td colSpan={6}>รวมทั้งสิ้น {doc.count} รายการ</td>
              <td colSpan={2} className="num">{fmt(doc.total)} บาท</td>
            </tr>
          </tbody>
        </table>

        {doc.remark ? <div className="remark"><b>หมายเหตุ:</b> {doc.remark}</div> : null}

        <div className="contact">
          <b>ผู้ติดตาม</b><span>{doc.contact || '—'}</span>
          <b>โทร</b><span>{doc.phones}</span>
          <b>อีเมล</b><span>{doc.email}</span>
        </div>
        <div className="small" style={{ marginTop: 6 }}>ค้าง (วัน) นับจากวันที่รักษาถึงวันที่ออกเอกสาร</div>
      </div>
    </div>
  )
}
